// @ts-nocheck
import {
  ArrowDownTrayIcon,
  ChevronDownIcon,
  EyeIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Breadcrumb from '../../components/breadcrumb/breadcrumb';
import { URL } from '../../service/Service';
import { getCustomerStatusLabel } from '../../utils/customerStatus';
import AccountBalance from '../AccountBalance';
import useFetchIDPartner from '../useFetchIDPartner';
import EditClientModalUser from './EditClientModalUser';
import { useTranslation } from '../../i18n/LanguageContext';

const normalizeExternalUrl = (url?: string) => {
  if (!url) return '';

  const value = url.trim();

  if (!value) return '';
  if (value.startsWith('http://') || value.startsWith('https://')) return value;

  return `https://${value}`;
};

const exportCsv = (rows, filename = 'clientes.csv') => {
  const headers = [
    'CID',
    'NIF',
    'Nome',
    'Website',
    'Estado',
    'Data Registo',
    'Último Levantamento',
    'Notas',
  ];

  const escape = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

  const body = rows.map((row) =>
      [
        row.customerID,
        row.nif,
        row.name,
        row.site,
        getCustomerStatusLabel(row.customerStatus),
        row.createAt,
        row.dateCalculate,
        row.notes,
      ]
          .map(escape)
          .join(';')
  );

  const csv = [headers.join(';'), ...body].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  link.click();

  window.URL.revokeObjectURL(url);
};

const statusClass = (status) => {
  switch (status) {
    case 'APPROVED':
      return 'bg-emerald-50 text-emerald-700 ring-emerald-200';
    case 'PENDING':
    case 'UPDATE_PENDING':
      return 'bg-amber-50 text-amber-700 ring-amber-200';
    case 'REJECTED':
      return 'bg-red-50 text-red-700 ring-red-200';
    default:
      return 'bg-gray-50 text-gray-600 ring-gray-200';
  }
};

export const Table = () => {
  const { t, language } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [clients, setClients] = useState([]);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage, setPostsPerPage] = useState(10);
  const [expandedClientID, setExpandedClientID] = useState(null);
  const [editClientID, setEditClientID] = useState(null);

  const jwtToken = sessionStorage.getItem('jwt');

  const pages = [
    {
      name: t('clients.breadcrumb'),
      href: '/user/clientes',
      current: true,
    },
  ];

  const { loading, partnerID } = useFetchIDPartner(jwtToken);

  const getFriendlyErrorMessage = useCallback(async (response) => {
    const fallback = t('clients.loadError');

    try {
      const text = await response.text();

      if (!text) {
        return fallback;
      }

      try {
        const json = JSON.parse(text);

        return (
            json?.message ||
            json?.error ||
            json?.detail ||
            fallback
        );
      } catch {
        return fallback;
      }
    } catch {
      return fallback;
    }
  }, [t]);

  useEffect(() => {
    const fetchClients = async () => {
      if (loading || !partnerID) return;

      try {
        setError(null);

        const response = await fetch(`${URL}/api/partners/${partnerID}/costumers/`, {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const message = await getFriendlyErrorMessage(response);
          setClients([]);
          setError(message);
          return;
        }

        const text = await response.text();

        if (!text) {
          setClients([]);
          setError(null);
          return;
        }

        const data = JSON.parse(text);

        if (!Array.isArray(data)) {
          setClients([]);
          setError(t('clients.loadError'));
          return;
        }

        setClients(data);
        setError(null);
      } catch (err) {
        console.error('Erro ao carregar clientes:', err);
        setClients([]);
        setError(
            t('clients.connectionError')
        );
      }
    };

    fetchClients();
  }, [loading, partnerID, jwtToken, getFriendlyErrorMessage, t]);

  const filteredClients = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();

    if (!q) return clients;

    return clients.filter((client) =>
        `${client.name || ''} ${client.customerID || ''} ${client.nif || ''} ${
            client.site || ''
        }`
            .toLowerCase()
            .includes(q)
    );
  }, [clients, searchTerm]);

  const totalPages = Math.max(
      1,
      Math.ceil(filteredClients.length / postsPerPage)
  );

  const currentClients =
      postsPerPage === 9999
          ? filteredClients
          : filteredClients.slice(
              (currentPage - 1) * postsPerPage,
              currentPage * postsPerPage
          );

  return (
      <div className="mx-4 md:mx-10">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Breadcrumb pages={pages} />
          <AccountBalance />
        </div>

        <section className="mt-8 rounded-3xl bg-white p-6 shadow-xl ring-1 ring-gray-100">
          <div className="flex flex-col gap-4 border-b border-gray-100 pb-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-green-cl">
                {t('clients.area')}
              </p>

              <h1 className="mt-1 text-3xl font-black text-gray-950">
                {t('clients.title')}
              </h1>

              <p className="mt-1 text-sm text-gray-500">
                {t('clients.description')}
              </p>
            </div>

            <button
                onClick={() => exportCsv(filteredClients, 'os-meus-clientes.csv')}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-bold text-gray-700 shadow-sm transition hover:bg-gray-50"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              {t('clients.export')}
            </button>
          </div>

          {error && (
              <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-700">
                {error}
              </div>
          )}

          <div className="mt-5 grid gap-3 md:grid-cols-[minmax(0,1fr)_190px]">
            <input
                type="search"
                placeholder={t('clients.search')}
                value={searchTerm}
                onChange={(event) => {
                  setSearchTerm(event.target.value);
                  setCurrentPage(1);
                }}
                className="h-14 w-full rounded-2xl border border-gray-300 bg-white px-5 text-base text-gray-900 shadow-sm outline-none transition placeholder:text-gray-400 focus:border-green-cl focus:ring-2 focus:ring-green-cl/20"
            />

            <div className="relative h-14 min-w-[190px]">
              <select
                  value={postsPerPage}
                  onChange={(event) => {
                    setPostsPerPage(Number(event.target.value));
                    setCurrentPage(1);
                  }}
                  style={{
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    backgroundImage: 'none',
                  }}
                  className="block h-14 w-full appearance-none rounded-2xl border-2 border-green-cl bg-white pl-5 pr-12 text-center text-sm font-bold leading-normal text-gray-950 shadow-sm outline-none transition focus:border-green-cl focus:ring-2 focus:ring-green-cl/20"
              >
                <option value={10}>10 {t('clients.perPage')}</option>
                <option value={25}>25 {t('clients.perPage')}</option>
                <option value={50}>50 {t('clients.perPage')}</option>
                <option value={9999}>{t('clients.showAll')}</option>
              </select>

              <ChevronDownIcon
                  className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-green-cl"
                  aria-hidden="true"
              />
            </div>
          </div>

          <div className="mt-5 overflow-hidden rounded-2xl border border-gray-100">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-green-cl text-white">
              <tr>
                <th title="Identificador do cliente" style={{ color: '#ffffff' }} className="px-5 py-4 text-left text-xs font-bold uppercase tracking-wide">
                  ID de Cliente (CID)
                </th>
                <th title="Número de Identificação Fiscal" style={{ color: '#ffffff' }} className="px-5 py-4 text-left text-xs font-bold uppercase tracking-wide">
                  NIF
                </th>
                <th style={{ color: '#ffffff' }} className="px-5 py-4 text-left text-xs font-bold uppercase tracking-wide">
                  {t('clients.client')}
                </th>
                <th style={{ color: '#ffffff' }} className="px-5 py-4 text-left text-xs font-bold uppercase tracking-wide">
                  {t('clients.website')}
                </th>
                <th style={{ color: '#ffffff' }} className="px-5 py-4 text-left text-xs font-bold uppercase tracking-wide">
                  {t('clients.status')}
                </th>
                <th style={{ color: '#ffffff' }} className="px-5 py-4 text-right text-xs font-bold uppercase tracking-wide">
                  {t('clients.actions')}
                </th>
              </tr>
              </thead>

              <tbody className="divide-y divide-gray-100 bg-white">
              {currentClients.length === 0 ? (
                  <tr>
                    <td
                        colSpan={6}
                        className="px-5 py-10 text-center text-sm text-gray-500"
                    >
                      {t('clients.none')}
                    </td>
                  </tr>
              ) : (
                  currentClients.map((client) => (
                      <React.Fragment key={client.customerID}>
                        <tr className="transition hover:bg-gray-50">
                          <td className="px-5 py-4 text-sm font-bold text-gray-950">
                            {client.customerID}
                          </td>

                          <td className="px-5 py-4 text-sm text-gray-700">
                            {client.nif || '-'}
                          </td>

                          <td className="px-5 py-4 text-sm text-gray-900">
                            {client.name}
                          </td>

                          <td className="px-5 py-4 text-sm">
                            {client.site ? (
                                <a
                                    className="font-semibold text-green-cl hover:underline"
                                    href={normalizeExternalUrl(client.site)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                  {client.site}
                                </a>
                            ) : (
                                '-'
                            )}
                          </td>

                          <td className="px-5 py-4">
                        <span
                            className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ring-1 ${statusClass(
                                client.customerStatusUpdate === 'UPDATE_PENDING'
                                    ? client.customerStatusUpdate
                                    : client.customerStatus
                            )}`}
                        >
                          {getCustomerStatusLabel(
                              client.customerStatusUpdate === 'UPDATE_PENDING'
                                  ? client.customerStatusUpdate
                                  : client.customerStatus,
                              undefined,
                              language
                          )}
                        </span>
                          </td>

                          <td className="px-5 py-4 text-right">
                            <div className="inline-flex items-center gap-2">
                              <button
                                  onClick={() =>
                                      setExpandedClientID(
                                          expandedClientID === client.customerID
                                              ? null
                                              : client.customerID
                                      )
                                  }
                                  className="portal-icon-action portal-icon-action-view"
                                  aria-label={t('clients.viewDetails')}
                                  title={t('clients.viewDetails')}
                              >
                                <EyeIcon className="h-4 w-4" />
                              </button>

                              <button
                                  onClick={() => setEditClientID(client.customerID)}
                                  disabled={client.customerStatusUpdate === 'UPDATE_PENDING'}
                                  className="portal-icon-action portal-icon-action-edit"
                                  aria-label={client.customerStatusUpdate === 'UPDATE_PENDING' ? t('clients.awaitingApproval') : t('clients.edit')}
                                  title={client.customerStatusUpdate === 'UPDATE_PENDING' ? t('clients.awaitingApproval') : t('clients.edit')}
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>

                        {expandedClientID === client.customerID && (
                            <tr className="bg-gray-50/70">
                              <td colSpan={6} className="px-5 py-5">
                                <div className="grid gap-4 rounded-2xl bg-white p-5 text-sm ring-1 ring-gray-100 md:grid-cols-3">
                                  <div>
                                    <p className="text-xs font-bold uppercase text-gray-400">
                                      {t('clients.registeredAt')}
                                    </p>
                                    <p className="mt-1 text-gray-900">
                                      {client.createAt || '-'}
                                    </p>
                                  </div>

                                  <div>
                                    <p className="text-xs font-bold uppercase text-gray-400">
                                      {t('clients.lastWithdrawal')}
                                    </p>
                                    <p className="mt-1 text-gray-900">
                                      {client.dateCalculate || '-'}
                                    </p>
                                  </div>

                                  <div>
                                    <p className="text-xs font-bold uppercase text-gray-400">
                                      {t('clients.changeStatus')}
                                    </p>
                                    <p className="mt-1 text-gray-900">
                                      {getCustomerStatusLabel(
                                          client.customerStatusUpdate,
                                          '-'
                                      )}
                                    </p>
                                  </div>

                                  <div className="md:col-span-3">
                                    <p className="text-xs font-bold uppercase text-gray-400">
                                      {t('clients.notes')}
                                    </p>
                                    <p className="mt-1 text-gray-900">
                                      {client.notes || '-'}
                                    </p>
                                  </div>
                                </div>
                              </td>
                            </tr>
                        )}
                      </React.Fragment>
                  ))
              )}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex flex-col gap-3 text-sm text-gray-500 sm:flex-row sm:items-center sm:justify-between">
            <span>{filteredClients.length} {t('clients.count')}</span>

            {postsPerPage !== 9999 && (
                <div className="flex items-center gap-2">
                  <button
                      disabled={currentPage <= 1}
                      onClick={() => setCurrentPage((page) => page - 1)}
                      className="rounded-lg border px-3 py-1 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {t('pagination.previous')}
                  </button>

                  <span className="px-2 py-1 font-semibold text-gray-700">
                {currentPage}/{totalPages}
              </span>

                  <button
                      disabled={currentPage >= totalPages}
                      onClick={() => setCurrentPage((page) => page + 1)}
                      className="rounded-lg border px-3 py-1 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {t('pagination.next')}
                  </button>
                </div>
            )}
          </div>
        </section>

        {editClientID && (
            <EditClientModalUser
                customerID={editClientID}
                partnerID={partnerID}
                onClose={() => setEditClientID(null)}
            />
        )}
      </div>
  );
};
